import { Button, PopupTooltip } from '@teamsnap/teamsnap-ui';
import { Column } from 'components/common/table/paginated-table';
import { displayLastNameFirst } from 'core/utils';
import { DateFormat, DateService } from 'frontend-toolkit';
import * as React from 'react';
import { Link } from 'react-router-dom';
import Member from '../../models/member';
import getActionsColumn from './getActionsColumn';

const emptyValue = () => <div key={'empty-val'}>&#8212;</div>;

const renderRegistration = (member: Member, linkRegistration: boolean) => {
  const registrations = member.seasonParticipants?.map(
    ({ registration }) => registration
  );

  return registrations?.length ? (
    <div>
      {registrations.map((registration) =>
        registration && registration.registrationFormName ? (
          <div key={registration.registrationFormName}>
            {linkRegistration ? (
              <a
                rel="noreferrer"
                key={`${member.id}${registration.id}`}
                target="_blank"
                href={
                  registration.orderId
                    ? `/organizations/${member.organizationId}/financials/order/${registration.orderId}/details`
                    : registration.registrationUrl
                }
              >
                {registration.registrationFormName}
              </a>
            ) : (
              <>{registration.registrationFormName}</>
            )}

            <strong>&nbsp;&middot;&nbsp;</strong>
            {registration?.registrationPaymentStatus ?? ''}
          </div>
        ) : (
          emptyValue()
        )
      )}
    </div>
  ) : (
    emptyValue()
  );
};

const manageInvites = (member: Member, memberLink: string) => {
  const isInvitable = member.seasonParticipants?.some(
    (sp) => sp.invitationStatus === 'is_invitable'
  );

  if (isInvitable) {
    return (
      <div>
        <a href={memberLink}>
          <Button className="Button Button--primary Button--small u-spaceTopSm">
            Manage Invites
          </Button>
        </a>
      </div>
    );
  }
};

const dashWhenEmptyArray = (x: any[]) => (x.length ? x : emptyValue());

const mapParticipantsToTable =
  (
    orgId: number,
    seasonId: number,
    programId: number,
    linkRegistration: boolean,
    customFields: Column[],
    setActionMembers: ([Member]) => void,
    setShowDeleteModal: (boolean) => void,
    setShowCopyModal: (boolean) => void,
    setShowTransferModal: (boolean) => void,
    setShowEditModal: (boolean) => void
  ) =>
  (member: Member) => {
    const [year, month, date] = member.birthdate?.split('-') || [];
    const memberLink = `/organizations/${orgId}/programs/${programId}/seasons/${seasonId}/participants/${member.id}`;
    return {
      ...(customFields || []).reduce(
        (prev, curr) => ({
          ...prev,
          [curr.name]: (
            <div>
              {dashWhenEmptyArray(
                member.seasonParticipants
                  .map((x) =>
                    x[curr.name] ? (
                      <p key={x.seasonParticipantId}>{x[curr.name]}</p>
                    ) : null
                  )
                  .filter((x) => !!x)
              )}
            </div>
          ),
        }),
        {}
      ),
      id: member.id,
      gender: member.gender ? member.gender : emptyValue(),
      actions: getActionsColumn(
        orgId,
        programId,
        seasonId,
        member,
        setActionMembers,
        setShowDeleteModal,
        setShowCopyModal,
        setShowTransferModal,
        setShowEditModal
      ),
      registration: renderRegistration(member, linkRegistration),
      city: !member.addresses.length ? (
        emptyValue()
      ) : (
        <div>
          {member.addresses.map(({ id, city }) => (
            <p key={id}>{city || emptyValue()}</p>
          ))}
        </div>
      ),
      state: !member.addresses.length ? (
        emptyValue()
      ) : (
        <div>
          {member.addresses.map(({ id, state }) => (
            <p key={id}>{state || emptyValue()}</p>
          ))}
        </div>
      ),
      zip: !member.addresses.length ? (
        emptyValue()
      ) : (
        <div>
          {member.addresses.map(({ id, postalCode }) => (
            <p key={id}>{postalCode || emptyValue()}</p>
          ))}
        </div>
      ),
      jerseyNumber: !member.seasonParticipants?.length ? (
        emptyValue()
      ) : (
        <div>
          {member.seasonParticipants.map(
            ({ seasonParticipantId, jerseyNumber }) => (
              <p key={seasonParticipantId}>{jerseyNumber || emptyValue()}</p>
            )
          )}
        </div>
      ),
      position: !member.seasonParticipants?.length ? (
        emptyValue()
      ) : (
        <div>
          {member.seasonParticipants.map(
            ({ seasonParticipantId, position }) => (
              <p key={seasonParticipantId}>{position || emptyValue()}</p>
            )
          )}
        </div>
      ),
      email: !member.emailAddresses.length ? (
        emptyValue()
      ) : (
        <div>
          {member.emailAddresses.map(({ id, email }) => (
            <p key={id}>{email}</p>
          ))}
        </div>
      ),
      phone: !member.phoneNumbers.length ? (
        emptyValue()
      ) : (
        <div>
          {member.phoneNumbers.map(({ id, number }) => (
            <p key={id}>{number}</p>
          ))}
        </div>
      ),
      birthdate: member.birthdate
        ? DateService.format(
            new Date(+year, +month - 1, +date),
            DateFormat.DD_MM_YYYY
          )
        : emptyValue(),
      name: (
        <div>
          <div>
            <Link to={memberLink}>{displayLastNameFirst(member)}</Link>
          </div>
          {manageInvites(member, memberLink)}
        </div>
      ),
      teamDivision: (
        <div>
          {member.seasonParticipants?.map(
            ({ seasonParticipantId, team, division, participationRoles }) => {
              const divisionName = division?.name ?? 'No Division';
              const teamName = team?.name ?? 'No Team';
              const roles =
                participationRoles && participationRoles.length ? (
                  <>
                    <strong>&nbsp;&middot;&nbsp;</strong>
                    {participationRoles[0].name}&nbsp;
                    {participationRoles.length >= 2 && (
                      <>
                        (
                        <PopupTooltip
                          text={participationRoles
                            ?.slice(1)
                            .map((participationRole) => participationRole.name)
                            .join(', ')}
                          ariaDescribeBy={`${member?.firstName} ${member?.lastName} ${member?.id} Roles`}
                        >
                          <div>+{participationRoles.length - 1}</div>
                        </PopupTooltip>
                        )
                      </>
                    )}
                  </>
                ) : (
                  ''
                );

              return (
                <p key={`${member.id}-${seasonParticipantId}-${division?.id}`}>
                  {`${teamName} (${divisionName})`}
                  {roles}
                </p>
              );
            }
          )}
        </div>
      ),
    };
  };

export default mapParticipantsToTable;
